// SCRUM-850 — env identity for the topbar badge. Derived from
// window.location.hostname so the badge renders on first paint with no
// network round-trip, no auth dependency, and no fragile nginx route.
// Hidden on prod (alphagroves.com); rendered on every non-prod host.
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

export interface EnvInfo {
  name: string;
  display_name: string;
  is_production: boolean;
}

const PROD_HOSTS: ReadonlySet<string> = new Set([
  'alphagroves.com',
  'www.alphagroves.com',
]);

const HOST_TO_ENV: Readonly<Record<string, { name: string; display_name: string }>> = {
  'dev.alphagroves.com': { name: 'dev', display_name: 'Development' },
  'uat.alphagroves.com': { name: 'uat', display_name: 'UAT' },
};

export function deriveEnvFromHostname(hostname: string): EnvInfo {
  const host = hostname.toLowerCase();
  if (PROD_HOSTS.has(host)) {
    return { name: 'prod', display_name: 'Production', is_production: true };
  }
  const mapped = HOST_TO_ENV[host];
  if (mapped) {
    return { ...mapped, is_production: false };
  }
  // Localhost, LAN IPs, *.trycloudflare.com quick tunnels, any other
  // unknown host: surface a visible "DEV" badge so it's obvious you are
  // NOT on production. Safer to over-label than to silently look like prod.
  return { name: 'dev', display_name: 'Development', is_production: false };
}

@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  readonly env$: Observable<EnvInfo | null> = of(
    typeof window === 'undefined' ? null : deriveEnvFromHostname(window.location.hostname),
  );
}
